import { useContext, useState } from "react";
import { Container, StyledButton } from "../../../components/styled/styled-components";
import Toast from "../../../components/Toast/Toast";
import { AlertType } from "../../../interfaces/enums";
import { AlertContent, ProductDescription } from "../../../interfaces/interfaces";
import { Brand } from "../../../interfaces/types";
import { Context, IContext } from "../../../context/ContextApp";
import { subdomainTypes } from "../../../context/availableSubdomains";
import Icon from "../../../components/Icon/icon";
import { dahliaShirts } from "./data";
import LinkButton, { LinkButtonType } from "../../../components/LinkButton/LinkButton";
import Button, { ButtonType } from "../../../components/Button/Button";

interface Props {
  stylesheetLink?: string;
  brand: Brand;
  slug: string;
  name: string;
  description?: ProductDescription;
}

const ProductLink = ({ stylesheetLink, brand, slug, description, name }: Props) => {
  const [toast, setToast] = useState<AlertContent | null>(null);
  const { appBranch }: IContext = useContext(Context);
  const productLink = generateProductLink(brand, name, slug, appBranch);

  const copyLinkToClipboard = async () => {
    if (!productLink) return;
    try {
      await navigator.clipboard.writeText(productLink);

      setToast({
        message: "Copied link to clipboard!",
        type: AlertType.Success,
      });
    } catch (error) {
      setToast({
        message: "Could not copy link to clipboard.",
        type: AlertType.Error,
      });
    }
  };

  return (
    <>
      {brand && slug && appBranch && (
        <>
          <Container row jcsb margin="0.5em 0 1rem 0" style={{ width: "100%" }}>
            {description?.product_detail_message?.toString().trim() && (
              <p className="product-detail-message">{description.product_detail_message}</p>
            )}
            <h6 style={{ display: "block", width: "100%" }}>Website links:</h6>
            {dahliaShirts.includes(slug) && (
              <a
                style={{ width: "48%" }}
                href="https://cdn.fashionbiz.com/EcoVero.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledButton style={{ width: "100%" }}>
                  <Container row jcsa aic>
                    EcoVera
                  </Container>
                </StyledButton>
              </a>
            )}
            {stylesheetLink ? (
              <LinkButton
                to={stylesheetLink || ""}
                target="_blank"
                type={LinkButtonType.secondary}
                className="product-details__spread"
                style={{ width: "100%" }}
              >
                <span>Stylesheet</span>
                <Icon name="ExternalLink" className="external-link" />
              </LinkButton>
            ) : (
              <Button disabled style={{ width: "100%" }}>
                NO STYLESHEET EXISTS
              </Button>
            )}
            {productLink && (
              <>
                <LinkButton
                  to={productLink}
                  target="_blank"
                  type={LinkButtonType.secondary}
                  className="product-details__spread"
                  style={{ width: "100%", margin: "0.5rem 0" }}
                >
                  <span>Website link</span>
                  <Icon name="ExternalLink" className="external-link" />
                </LinkButton>
                <Button
                  onClick={() => copyLinkToClipboard()}
                  type={ButtonType.secondary}
                  className="product-details__spread"
                  style={{ width: "100%" }}
                >
                  <span>Copy to clipboard</span>
                  <Icon name="Clipboard" />
                </Button>
              </>
            )}
          </Container>
          <Toast content={toast} onClick={() => setToast(null)} />
        </>
      )}
    </>
  );
};

export const generateProductLink = (
  brand: Brand,
  name: string,
  slug: string,
  appBranch: subdomainTypes | "",
) => {
  const countryMap: Record<subdomainTypes, string> = {
    [subdomainTypes.NZ]: "fashionbiz.co.nz",
    [subdomainTypes.AU]: "fashionbiz.com.au",
    [subdomainTypes.CA]: "fashionbiz.ca",
  };

  const domain = appBranch && countryMap[appBranch];

  const url = `https://www.${domain}/product/${slug}`;

  switch (brand) {
    case "syzmik":
      if (appBranch !== subdomainTypes.CA) {
        const syzmikURL =
          appBranch === subdomainTypes.AU ? `https://www.syzmik.com` : `https://www.syzmik.co.nz`;
        return `${syzmikURL}/product/${slug}`;
      }
      return url;
    case "syzmik-boots":
      if (appBranch !== subdomainTypes.CA) {
        return `https://www.syzmik.com/product/${slug}`;
      }
      return url;
    case "good-mates":
      if (appBranch !== subdomainTypes.CA) {
        const goodmatesDomain: Partial<Record<subdomainTypes, string>> = {
          [subdomainTypes.NZ]: ".co.nz",
          [subdomainTypes.AU]: ".com.au",
        };

        return `https://goodmatesapparel${appBranch && goodmatesDomain[appBranch]}/search?q=${name}`;
      }
      return url;
    default:
      return url;
  }
};

export default ProductLink;
